import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, getAuth, clearAuth } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Trash2 } from "lucide-react";
import { useRevealAboveKeyboard } from "@/lib/useRevealAboveKeyboard";
import { useTenant } from "./TenantRoot";

/** Account → Security. Holds the customer's Delete Account action. */
export default function TenantSecurity() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { tenant } = useTenant();
  const user = getAuth()?.user;
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const confirmRef = useRef(null);
  const appName = tenant.branding?.app_name || tenant.business_name;

  useEffect(() => {
    if (!user || user.role !== "customer") nav(`/t/${slug}/login?next=/t/${slug}/account/security`, { replace: true });
  }, [user, slug, nav]);

  // Keep the whole confirmation (field and both buttons) above the on-screen keyboard.
  useRevealAboveKeyboard(confirmRef, confirming);

  if (!user) return null;


  const deleteAccount = async () => {
    setBusy(true);
    try {
      const r = await api.delete(`/customer/${slug}/account`);
      clearAuth();
      const n = r.data.cancelled_bookings;
      toast.success(n ? `Account deleted. ${n} upcoming booking${n === 1 ? "" : "s"} cancelled.` : "Account deleted.");
      nav(`/t/${slug}`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete the account. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="pwa-narrow px-5 py-5 md:py-8" data-testid="tenant-security-page">
      <h1 className="brand-heading font-bold text-2xl md:text-3xl">Security</h1>
      <p className="text-sm brand-text-muted mt-1 break-anywhere">Signed in as {user.phone || user.name}</p>

      <div className="brand-card p-5 mt-5">
        <div className="flex items-start gap-3">
          <Trash2 className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: "#B91C1C" }} />
          <div className="min-w-0">
            <div className="font-bold">Delete account</div>
            <ul className="text-sm brand-text-muted mt-2 space-y-1.5 list-disc pl-4">
              <li>You are signed out and your {appName} account is closed.</li>
              <li>Your upcoming bookings are cancelled.</li>
              <li>Records of past bookings are kept by the business as required for its accounts.</li>
              <li>You can sign up again later with the same mobile number. That starts a new account.</li>
            </ul>
            <Link to={`/t/${slug}/account-deletion`} className="inline-block text-sm underline mt-3 py-1" data-testid="tenant-security-deletion-info">
              What is deleted and what is kept
            </Link>
          </div>
        </div>

        {!confirming ? (
          <button onClick={() => setConfirming(true)} className="mt-5 w-full py-3 rounded-full font-semibold text-sm border-2"
            style={{ borderColor: "#B91C1C", color: "#B91C1C" }} data-testid="tenant-delete-account-btn">
            Delete my account
          </button>
        ) : (
          <div ref={confirmRef} className="mt-5 rounded-xl p-4" style={{ background: "#FEF2F2", color: "#7F1D1D" }} data-testid="tenant-delete-account-confirm">
            <div className="flex items-center gap-2 font-bold text-sm"><AlertTriangle className="h-4 w-4 flex-shrink-0" /> This cannot be undone</div>
            <label className="block text-xs mt-3" htmlFor="delete-confirm">Type DELETE to confirm</label>
            <input id="delete-confirm" value={typed} onChange={(e) => setTyped(e.target.value)} autoCapitalize="characters" autoComplete="off"
              className="w-full mt-1 bg-white border border-red-300 rounded-xl px-4 py-3 text-base text-black focus:outline-none" data-testid="tenant-delete-account-input" />
            <div className="flex gap-2 mt-3">
              <button onClick={() => { setConfirming(false); setTyped(""); }} className="flex-1 bg-white border border-red-200 py-3 rounded-full font-semibold text-sm text-black">Keep my account</button>
              <button onClick={deleteAccount} disabled={busy || typed.trim().toUpperCase() !== "DELETE"}
                className="flex-1 py-3 rounded-full font-semibold text-sm text-white disabled:opacity-40" style={{ background: "#B91C1C" }} data-testid="tenant-delete-account-confirm-btn">
                {busy ? "Deleting..." : "Delete account"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
